/**
 * ESCHER FALLS — プレイ中の音（BGM）。**合成のみ。音声ファイルは持たない。**
 *
 * ## 旋律を書かない理由
 * この遊びは盤面をじっと見て考えるものである。旋律には拍と展開があり、
 * どちらも思考の邪魔になる。何度も同じ面を見比べるので、**繰り返しに気づかれた
 * 瞬間に音楽は雑音になる**。
 *
 * したがって旋律・拍・繰り返しを持たない「場の音」にしてある。
 * - 低い持続音を2本、わずかに離調させて重ねる（うなりで動きが出る）
 * - 5度を1本足す。3度を入れると長短が決まって表情が付きすぎる
 * - 通す濾波器をごく遅く開閉させて、息をしているようにする
 * - ごく小さい空気の音を混ぜて、合成音の平坦さを消す
 *
 * ## 水滴を撒くのをやめた経緯
 * 当初は「水の城に居る合図」として不定間隔で水滴を落としていたが、
 * **何もしていないのに「ピュン」と鳴る**として不要と判断された。もっともで、
 * 盤面をじっと見て考えている最中に**出来事が起きる**こと自体が邪魔である。
 * 間隔を不規則にしても、鳴ること自体が意識を引く。
 *
 * したがってこの音には**discrete な出来事を一切置かない。**
 * 動きは持続音のうなりと濾波器の呼吸だけで作る——どちらも始まりと終わりを
 * 持たないので、注意を引かない。ここに何かを足したくなったら、
 * まず「それは出来事か」を問うこと。出来事なら置いてはいけない。
 */

import { audioContext, bgmBus, noise } from './engine';

/** 根音。低めに置く。ここが高いと「音楽」に聞こえ始める。 */
const ROOT_HZ = 61.74; // B1

let nodes: {
  oscs: OscillatorNode[];
  filter: BiquadFilterNode;
  breath: OscillatorNode;
  air: AudioBufferSourceNode;
  gain: GainNode;
} | null = null;

/** 鳴らし始める。すでに鳴っていれば何もしない。 */
export function startBgm(): void {
  if (nodes !== null) return;
  const ctx = audioContext();
  const out = bgmBus();
  if (ctx === null || out === null) return;
  const t = ctx.currentTime;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  // 立ち上がりを長くとる。ぱっと鳴り出すと「BGM が始まった」と意識されてしまう。
  gain.gain.exponentialRampToValueAtTime(0.12, t + 4);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 420;
  filter.Q.value = 0.6;

  // 息づかい。40 秒で一周する——気づかれない速さにする。
  const breath = ctx.createOscillator();
  breath.frequency.value = 1 / 40;
  const breathDepth = ctx.createGain();
  breathDepth.gain.value = 180;
  breath.connect(breathDepth).connect(filter.frequency);

  // 持続音3本。根音を2本わずかに離調（うなり）、5度を1本。
  const oscs: OscillatorNode[] = [];
  for (const [ratio, detune, level, type] of [
    [1, -4, 0.5, 'sine'],
    [1, 5, 0.45, 'sine'],
    [1.5, 0, 0.22, 'triangle'],
  ] as Array<[number, number, number, OscillatorType]>) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = ROOT_HZ * ratio;
    osc.detune.value = detune;
    const g = ctx.createGain();
    g.gain.value = level;
    osc.connect(g).connect(filter);
    osc.start(t);
    oscs.push(osc);
  }

  // 空気。ごく小さい雑音を混ぜると、合成音の平坦さが消える。
  const air = ctx.createBufferSource();
  air.buffer = noise(ctx);
  air.loop = true;
  const airBand = ctx.createBiquadFilter();
  airBand.type = 'bandpass';
  airBand.frequency.value = 1400;
  airBand.Q.value = 0.5;
  const airGain = ctx.createGain();
  airGain.gain.value = 0.012;
  air.connect(airBand).connect(airGain).connect(gain);
  air.start(t);

  filter.connect(gain);
  gain.connect(out);
  breath.start(t);

  nodes = { oscs, filter, breath, air, gain };
}

/** 止める。鳴っていなければ何もしない。 */
export function stopBgm(): void {
  const ctx = audioContext();
  if (nodes === null || ctx === null) {
    nodes = null;
    return;
  }
  const { oscs, breath, air, gain } = nodes;
  nodes = null;
  const t = ctx.currentTime;
  gain.gain.cancelScheduledValues(t);
  gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
  for (const osc of oscs) osc.stop(t + 1.4);
  breath.stop(t + 1.4);
  air.stop(t + 1.4);
}

export function isBgmPlaying(): boolean {
  return nodes !== null;
}
